import { motion } from "framer-motion";
import { useContent } from "@/context/ContentContext";
import { Link } from "react-router-dom";
import { ArrowUpRight, Mic } from "lucide-react";

export default function Speaking() {
  const { talks } = useContent();

  return (
    <div className="max-w-7xl mx-auto px-6 lg:px-10 py-16 lg:py-24" data-testid="speaking-page">
      <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-muted-foreground mb-6">[ 04 · speaking ]</div>
      <div className="grid lg:grid-cols-12 gap-10 items-end">
        <h1 className="lg:col-span-8 font-serif text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.05]">
          On the stage. In the pulpit. In the boardroom.
        </h1>
        <p className="lg:col-span-4 text-muted-foreground">
          A partial list of recent keynotes, conference talks, and teaching series. Every talk is written for a specific audience — never recycled.
        </p>
      </div>

      <div className="mt-16 border-t border-border">
        {talks.map((t, i) => (
          <motion.article
            key={t.id}
            initial={{ opacity: 0, y: 15 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.5 }}
            className="group grid lg:grid-cols-12 gap-6 py-10 border-b border-border"
            data-testid={`talk-item-${i}`}
          >
            <div className="lg:col-span-2 font-mono text-[10px] uppercase tracking-[0.25em] text-muted-foreground">
              [ 0{i + 1} ]<br />
              <span className="text-foreground/70">{t.date}</span>
            </div>
            <div className="lg:col-span-7">
              <h2 className="font-serif text-2xl sm:text-3xl tracking-tight">{t.title}</h2>
              <div className="font-mono text-xs uppercase tracking-wider text-accent mt-2">{t.venue}</div>
              <p className="text-muted-foreground mt-4 leading-relaxed">{t.summary}</p>
            </div>
            <ul className="lg:col-span-3 space-y-2">
              {(t.highlights || []).map((h, idx) => (
                <li key={idx} className="flex items-start gap-2 text-xs">
                  <span className="text-accent font-mono">→</span>
                  <span className="text-muted-foreground">{h}</span>
                </li>
              ))}
            </ul>
          </motion.article>
        ))}
      </div>

      <div className="mt-16 p-10 lg:p-16 rounded-3xl border border-border bg-card">
        <div className="grid lg:grid-cols-12 gap-8 items-center">
          <div className="lg:col-span-8">
            <div className="flex items-center gap-3 font-mono text-[10px] uppercase tracking-[0.3em] text-muted-foreground mb-4">
              <Mic className="w-3.5 h-3.5" /> [ book me ]
            </div>
            <h3 className="font-serif text-3xl sm:text-4xl tracking-tight">Bring me to your stage, staff, or sanctuary.</h3>
            <p className="mt-4 text-muted-foreground max-w-xl">
              Keynotes, half-day workshops, tabletop facilitation, and multi-part teaching series. I tailor every engagement to the audience — no recycled decks.
            </p>
          </div>
          <div className="lg:col-span-4 lg:justify-self-end flex flex-col items-start lg:items-end gap-3">
            <Link to="/speaking/book" className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-foreground text-background font-mono text-xs uppercase tracking-[0.2em]" data-testid="book-speaker-cta">
              Book a conversation <ArrowUpRight className="w-4 h-4" />
            </Link>
            <Link
              to="/contact"
              data-testid="speaking-contact-link"
              className="font-mono text-[11px] uppercase tracking-[0.25em] text-muted-foreground link-underline"
            >
              or send me a direct message →
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
